import React, { useRef, useEffect, FormEvent } from 'react';
import Modal from 'react-bootstrap/Modal';
import {
  IonItem,
  IonLabel,
  IonRow,
  IonCol,
  IonButton,
  IonInput,
  IonTextarea,
  IonText,
  IonList,
  IonCardContent,
} from '@ionic/react';
import useAccessibilityForm from '../hooks/useAccessibilityForm';
import ThankYou from './ThankYou';

// Type assertion to work around react-bootstrap type incompatibility
const ModalHeader = Modal.Header as React.FC<{ closeButton?: boolean; children?: React.ReactNode }>;

interface AccessibilityModalProps {
  show: boolean;
  onHide: () => void;
}

const AccessibilityModal: React.FC<AccessibilityModalProps> = ({ show, onHide }) => {
  const { handleChange, handleBlur, handleSubmit, values, errors, isSubmitting } = useAccessibilityForm();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap - focus the close button when modal opens
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      dialogClassName="modal-component"
      aria-labelledby="accessibility-modal-title"
      aria-describedby="accessibility-modal-description"
      centered
      scrollable
      role="dialog"
      aria-modal="true"
    >
      <ModalHeader closeButton>
        <Modal.Title id="accessibility-modal-title">Accessibility</Modal.Title>
      </ModalHeader>
      <Modal.Body>
        <IonCardContent className="ion-no-padding">
          <p id="accessibility-modal-description">
            The developers of this website strive to ensure that its services are accessible to people with
            disabilities, with the strong belief that every person has the right to live with dignity, equality,
            comfort and independence both in life, and on the internet.
          </p>
          <br />
          <p>
            If you are unable to use any aspect of this website, PDF files, or other attachments, we can provide you
            with prompt assistance. Please let us know by filling out the form below or by emailing{' '}
            <a href="mailto:ada-support@valerin-group.com" aria-label="Send email to ADA support">
              ada-support@valerin-group.com
            </a>
          </p>
        </IonCardContent>
        {!isSubmitting ? (
          <form
            onSubmit={handleSubmit as unknown as (e: FormEvent<HTMLFormElement>) => void}
            id="accessibilityForm"
            aria-label="Accessibility support request form"
          >
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend className="sr-only">Accessibility Support Request</legend>
              {/* Announce errors to screen readers */}
              <div aria-live="polite" aria-atomic="true" className="sr-only">
                {Object.keys(errors).length > 0 &&
                  `Form has ${Object.keys(errors).length} error(s). Please correct them.`}
              </div>
              <IonList className="project-list">
                <IonRow>
                  <IonCol>
                    <IonItem mode="md">
                      <IonLabel position="floating" id="a11y-firstName-label">
                        First Name
                      </IonLabel>
                      <IonInput
                        type="text"
                        name="firstName"
                        value={values.firstName}
                        onIonChange={handleChange as any}
                        onIonBlur={handleBlur as any}
                        aria-labelledby="a11y-firstName-label"
                        aria-describedby={errors.firstName ? 'a11y-firstName-error' : undefined}
                        aria-invalid={errors.firstName ? 'true' : 'false'}
                        aria-required="true"
                        autocomplete="given-name"
                      />
                    </IonItem>
                    {errors.firstName && (
                      <p
                        id="a11y-firstName-error"
                        role="alert"
                        style={{
                          fontSize: '13px',
                          marginLeft: '16px',
                          color: 'var(--ion-color-danger)',
                        }}
                      >
                        {errors.firstName}
                      </p>
                    )}
                  </IonCol>
                  <IonCol>
                    <IonItem mode="md">
                      <IonLabel position="floating" id="a11y-lastName-label">
                        Last Name
                      </IonLabel>
                      <IonInput
                        type="text"
                        name="lastName"
                        value={values.lastName}
                        onIonChange={handleChange as any}
                        onIonBlur={handleBlur as any}
                        aria-labelledby="a11y-lastName-label"
                        aria-describedby={errors.lastName ? 'a11y-lastName-error' : undefined}
                        aria-invalid={errors.lastName ? 'true' : 'false'}
                        aria-required="true"
                        autocomplete="family-name"
                      />
                    </IonItem>
                    {errors.lastName && (
                      <p
                        id="a11y-lastName-error"
                        role="alert"
                        style={{
                          fontSize: '13px',
                          marginLeft: '16px',
                          color: 'var(--ion-color-danger)',
                        }}
                      >
                        {errors.lastName}
                      </p>
                    )}
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol>
                    <IonItem mode="md">
                      <IonLabel position="floating" id="a11y-phoneNumber-label">
                        Phone Number
                      </IonLabel>
                      <IonInput
                        name="phoneNumber"
                        type="tel"
                        value={values.phoneNumber}
                        onIonChange={handleChange as any}
                        onIonBlur={handleBlur as any}
                        aria-labelledby="a11y-phoneNumber-label"
                        aria-describedby={errors.phoneNumber ? 'a11y-phoneNumber-error' : undefined}
                        aria-invalid={errors.phoneNumber ? 'true' : 'false'}
                        aria-required="true"
                        autocomplete="tel"
                      />
                    </IonItem>
                    {errors.phoneNumber && (
                      <p
                        id="a11y-phoneNumber-error"
                        role="alert"
                        style={{
                          fontSize: '13px',
                          marginLeft: '16px',
                          color: 'var(--ion-color-danger)',
                        }}
                      >
                        {errors.phoneNumber}
                      </p>
                    )}
                  </IonCol>
                  <IonCol>
                    <IonItem mode="md">
                      <IonLabel position="floating" id="a11y-email-label">
                        Email Address
                      </IonLabel>
                      <IonInput
                        name="email"
                        type="email"
                        value={values.email}
                        onIonChange={handleChange as any}
                        onIonBlur={handleBlur as any}
                        aria-labelledby="a11y-email-label"
                        aria-describedby={errors.email ? 'a11y-email-error' : undefined}
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-required="true"
                        autocomplete="email"
                      />
                    </IonItem>
                    {errors.email && (
                      <p
                        id="a11y-email-error"
                        role="alert"
                        style={{
                          fontSize: '13px',
                          marginLeft: '16px',
                          color: 'var(--ion-color-danger)',
                        }}
                      >
                        {errors.email}
                      </p>
                    )}
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol>
                    <IonItem mode="md">
                      <IonLabel position="floating" id="a11y-message-label">
                        Message
                      </IonLabel>
                      <IonTextarea
                        name="message"
                        value={values.message}
                        onIonChange={handleChange as any}
                        onIonBlur={handleBlur as any}
                        aria-labelledby="a11y-message-label"
                        aria-describedby={errors.message ? 'a11y-message-error' : undefined}
                        aria-invalid={errors.message ? 'true' : 'false'}
                        aria-required="true"
                      />
                    </IonItem>
                    {errors.message && (
                      <p
                        id="a11y-message-error"
                        role="alert"
                        style={{
                          fontSize: '13px',
                          marginLeft: '16px',
                          color: 'var(--ion-color-danger)',
                        }}
                      >
                        {errors.message}
                      </p>
                    )}
                    <IonButton
                      mode="ios"
                      fill="solid"
                      size="default"
                      color="secondary"
                      className="ion-margin-top ion-margin-bottom button-text-white"
                      type="submit"
                      aria-label="Send accessibility support message"
                    >
                      Send Message
                    </IonButton>
                    <br />
                    <IonText color="medium">
                      <p>
                        Under Florida law, e-mail addresses are public records. If you do not want your e-mail address
                        released in response to a public-records request, do not send electronic mail to this entity.
                        Instead, contact this office by phone or in writing.
                      </p>
                    </IonText>
                  </IonCol>
                </IonRow>
              </IonList>
            </fieldset>
          </form>
        ) : (
          <ThankYou />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AccessibilityModal;
